import { router } from '@inertiajs/svelte'
import type { VisitCallbacks } from '@inertiajs/core'
import { trackEvent } from 'frontend/shared/javascripts/util/analytics'
import {
  flushNextPageEvents,
  trackOnNextPage,
  type EventData,
  type TrackEventOverrides,
} from 'frontend/shared/javascripts/tracking'
import { isInertiaUrl } from 'shared/javascripts/util/inertia_paths'
import { assignLocation } from 'shared/javascripts/util/navigation'

// Same shape as AnalyticsHelper#relative_path_or_remote_url on Unpoly pages: own URLs as path and
// query, without the fragment, which browsers never send to the server.
function relativePathOrRemoteUrl(url: string): string | null {
  if (!url) return null

  let parsed: URL
  try {
    parsed = new URL(url, location.href)
  } catch {
    return null
  }

  return parsed.hostname === location.hostname ? parsed.pathname + parsed.search : parsed.href
}

export function trackInertiaPageViews(): () => void {
  let previousUrl: string | null = null

  return router.on('navigate', (event) => {
    const { page } = event.detail
    const referrer = relativePathOrRemoteUrl(previousUrl ?? document.referrer)
    previousUrl = page.url

    // `navigate` fires after the swap, so the new page's title is already applied.
    const eventData = {
      // Only error pages carry a `status` prop (DoesRenderInertiaErrors), everything else renders with a 200.
      status: typeof page.props.status === 'number' ? page.props.status : 200,
      path: relativePathOrRemoteUrl(page.url),
      referrer,
      percentage: 0,
      page: {
        title: document.title,
        modal: false,
      },
    }

    trackEvent('page.view', eventData)
    flushNextPageEvents()
  })
}

export interface VisitEvent {
  event: string
  data?: EventData
  overrides?: TrackEventOverrides
}

// Pushes `events` once the visit has landed, or on the next page if the server ends it in a full page load.
export function trackAfterVisit(
  events: VisitEvent[],
): Pick<VisitCallbacks, 'onBefore' | 'onSuccess' | 'onFinish'> {
  let stopWatching: (() => void) | undefined

  return {
    onBefore: () => {
      stopWatching = router.on('location', () => pushAll(events, trackOnNextPage))
    },
    onSuccess: () => pushAll(events, trackEvent),
    onFinish: () => {
      stopWatching?.()
      stopWatching = undefined
    },
  }
}

export function visitAndTrack(url: string, events: VisitEvent[]): void {
  if (isInertiaUrl(new URL(url, window.location.href))) {
    router.visit(url, trackAfterVisit(events))
  } else {
    // boundary.ts loads it in full, without a `location` event.
    pushAll(events, trackOnNextPage)
    assignLocation(url)
  }
}

function pushAll(
  events: VisitEvent[],
  push: (event: string, data: EventData, options: { overrides?: TrackEventOverrides }) => void,
): void {
  for (const { event, data = {}, overrides } of events) {
    push(event, data, { overrides })
  }
}
